/**
 * 画面とループの組み立て。
 * ここは render 側なので、sim の状態は読むだけ・進めるだけ（§4.4）。
 */
import { EMPTY, NO_FURNITURE, TILE, furnitureAt, isPlayerPlaced, tileAt } from './sim/grid';
import {
  Brush,
  ClimbCheck,
  FloorYield,
  Issue,
  checkClimbable,
  exportFloors,
  exportMemories,
  floorYields,
  paint,
  paintRect,
  validate,
} from './sim/editor';
import { BUILDING, GameState, createInitialState } from './sim/state';
import { FIXED_DT, tick } from './sim/tick';
import { ITEMS, ItemId, itemCells, itemCost, itemDef, itemHasFacing } from './sim/items';
import { MATERIALS, MATERIAL_IDS, describeBundle } from './sim/materials';
import {
  World,
  addCat as addCatToWorld,
  canPlaceItem,
  craft,
  createWorld,
  graphFor,
  inventoryCount,
  isSolidItem,
  pickUpNear,
  place,
  reachableFor,
  remove,
  setSpeciesParams,
  undo as undoWorld,
} from './sim/world';
import {
  StaticLayer,
  bakeStatic,
  drawBeds,
  drawBowl,
  drawEntrance,
  drawFloorLabels,
  drawPlaced,
  drawBuilds,
  drawDecor,
  drawDrops,
  drawFixtures,
  drawMemories,
  drawSalvage,
  drawSky,
  drawSun,
  drawTraces,
  drawWater,
} from './render/layers';
import { catHit, drawCat, drawFollowMark, drawWaitingCat } from './render/cats';
import { drawMood, drawName, moodOf } from './render/mood';
import {
  Flash,
  OverlayOptions,
  drawCatPath,
  drawCursor,
  drawHistoryLine,
  drawFlashes,
  drawGraph,
  drawTraffic,
} from './render/overlay';
import {
  Camera,
  centerOn,
  createCamera,
  cycleZoom,
  flingBy,
  isZoomed,
  panBy,
  screenToWorld,
  stopFollowing,
  updateCamera,
  worldToCell,
  zoomLabel,
} from './render/camera';
import { AUTO_SLOT, listSlots, loadSlot, saveSlot } from './save/db';

/*
 * 道具（デバッグパネル）は**どのビルドにも入っている**。ビルドの種類で出し分けない。
 * 想定プレイヤーが作者本人（§1）で、§4.6 が Web を選んでいる理由が
 * 「URLを開くだけで触れる。作者がスマホからでも直せる」である以上、
 * 置いた物から道具が消えているとその理由自体が成り立たない。
 *
 * §4.2「React はゲーム画面に使わない」は動的 import で守られている。
 * 「道具」を押すまで React のチャンクは取りに行かないので、遊ぶだけなら降ってこない。
 */
const EDIT_HASH = location.hash === '#edit';
// 最初から開いておくか。開発中と、URL に #debug / #edit が付いている時
const DEBUG_AT_BOOT = import.meta.env.DEV || /^#(debug|edit)$/.test(location.hash);
// 跳躍グラフの重ね描きは、パネルが出ている間だけ効く
let DEBUG_ENABLED = DEBUG_AT_BOOT;

const canvas = document.getElementById('cv') as HTMLCanvasElement;
const ctx = canvas.getContext('2d')!;
const stage = canvas.parentElement as HTMLElement;
const toastEl = document.getElementById('toast') as HTMLElement;
const catCountEl = document.getElementById('catcount') as HTMLElement;
const materialsEl = document.getElementById('materials') as HTMLElement;
const inventoryEl = document.getElementById('inventory') as HTMLElement;
const recipesEl = document.getElementById('recipes') as HTMLElement;
const craftSheet = document.getElementById('craft-sheet') as HTMLElement;
const craftOpenBtn = document.getElementById('craft-open') as HTMLButtonElement;
const foundSheet = document.getElementById('found-sheet') as HTMLElement;
const foundOpenBtn = document.getElementById('found-open') as HTMLButtonElement;
const foundCloseBtn = document.getElementById('found-close') as HTMLButtonElement;
const foundListEl = document.getElementById('found-list') as HTMLElement;
const craftCloseBtn = document.getElementById('craft-close') as HTMLButtonElement;
const undoBtn = document.getElementById('undo') as HTMLButtonElement;
const flipBtn = document.getElementById('flip') as HTMLButtonElement;
const zoomBtn = document.getElementById('zoom') as HTMLButtonElement;
const toolsBtn = document.getElementById('tools') as HTMLButtonElement;
const debugRoot = document.getElementById('debug-root') as HTMLElement;

let world: World = createWorld(createInitialState());
let staticLayer: StaticLayer = bakeStatic(world.state);
const cam: Camera = createCamera();
let flashes: Flash[] = [];
let hover: { c: number; r: number } | null = null;
/** 到着したばかりの猫の来歴を、しばらく表示しておくための残り秒 */
const historyFade = new Map<number, number>();

/** 速度は「1フレームに何回 tick するか」。DT は変えない（§4.5） */
let speed = 1;
let selectedCatId: number | null = null;
/** 棚（インベントリ）から選んでいる物。空なら置くモードではない */
let heldItem: ItemId | null = null;
/**
 * 複数マスの物は指では置き場所が読めない。
 * 1回目のタップで置き場所を見せて、同じ所をもう一度押したら建てる（§5.3 誤タップ対策）。
 */
let ghost: { c: number; r: number } | null = null;
/**
 * ステージ編集の筆（§6）。null でなければ、タップは設置ではなく地形の編集になる。
 * 本番ビルドではデバッグパネルごと落ちるので、ここが null のまま触られない。
 */
let editBrush: Brush | null = null;
/** 範囲塗りモードと、押した1つ目の角 */
let editRect = false;
let rectFrom: { c: number; r: number } | null = null;

const view: OverlayOptions & { species: string } = {
  showEdges: false,
  showReach: DEBUG_ENABLED,
  showNodes: false,
  showTraffic: false,
  kinds: { walk: true, up: true, fall: true, climb: true },
  species: 'stray',
};

// ---- toast ------------------------------------------------------------
let toastTimer: number | undefined;
function toast(msg: string): void {
  toastEl.textContent = msg;
  toastEl.classList.add('on');
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => toastEl.classList.remove('on'), 1600);
}

// ---- サイズ -----------------------------------------------------------
let cssW = 0;
let cssH = 0;
let dpr = 1;

function resize(): void {
  dpr = Math.min(window.devicePixelRatio || 1, 2);
  cssW = stage.clientWidth;
  // ビルが縦に長いので、画面の高さをできるだけ使う
  cssH = Math.max(320, Math.min(window.innerHeight * 0.74, 900));
  canvas.style.height = `${Math.round(cssH)}px`;
  canvas.width = Math.round(cssW * dpr);
  canvas.height = Math.round(cssH * dpr);
}
window.addEventListener('resize', resize);

// ---- 入力（Pointer Events / §5.3）-------------------------------------
interface Touch {
  x: number;
  y: number;
  startX: number;
  startY: number;
  /**
   * 直近の動き。フリックの速度はここから出す。
   * 最後の1フレームだけで測ると、指を離す直前の減速や取りこぼしを拾ってしまい、
   * 「振ったのに滑らない」になる。少し前の点との差で測る。
   */
  trail: { x: number; y: number; t: number }[];
  moved: boolean;
}
const touches = new Map<number, Touch>();
/** ピンチ開始時の指の間隔と倍率段階 */
let pinchStart: number | null = null;
const DRAG_THRESHOLD = 8;
const DOUBLE_TAP_MS = 300;
/** 速度を測る窓（ms）。これより古い点は捨てる */
const FLICK_WINDOW_MS = 110;
/** 指が止まってから離した時に滑らせないための下限（ms） */
const FLICK_MIN_MS = 25;
let lastTapAt = 0;

function pointerPair(): [Touch, Touch] | null {
  if (touches.size !== 2) return null;
  const [a, b] = [...touches.values()];
  return [a, b];
}

canvas.addEventListener('pointerdown', (ev) => {
  ev.preventDefault();
  canvas.setPointerCapture(ev.pointerId);
  const now = performance.now();
  touches.set(ev.pointerId, {
    x: ev.clientX,
    y: ev.clientY,
    startX: ev.clientX,
    startY: ev.clientY,
    trail: [{ x: ev.clientX, y: ev.clientY, t: now }],
    moved: false,
  });
  // 触った瞬間に滑りは止める
  cam.vx = 0;
  cam.vy = 0;
  const pair = pointerPair();
  if (pair) pinchStart = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
});

canvas.addEventListener('pointermove', (ev) => {
  const t = touches.get(ev.pointerId);
  const w = screenToWorld(canvas, cam, ev.clientX, ev.clientY);
  hover = worldToCell(w.x, w.y);
  if (!t) return;

  const now = performance.now();
  const dx = ev.clientX - t.x;
  const dy = ev.clientY - t.y;
  t.x = ev.clientX;
  t.y = ev.clientY;
  if (!t.moved && Math.hypot(ev.clientX - t.startX, ev.clientY - t.startY) > DRAG_THRESHOLD) {
    t.moved = true;
  }

  const pair = pointerPair();
  if (pair && pinchStart !== null) {
    // 2本指：広げると拡大、縮めると縮小
    const d = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
    if (d / pinchStart > 1.35) {
      if (!isZoomed(cam) || cam.zoom % 3 === 1) cycleZoom(cam);
      pinchStart = d;
      zoomBtn.textContent = zoomLabel(cam);
    } else if (pinchStart / d > 1.35) {
      cam.zoom = (cam.zoom + 2) % 3;
      pinchStart = d;
      zoomBtn.textContent = zoomLabel(cam);
    }
    return;
  }

  if (!t.moved) return;
  // 1本指のドラッグ：指に貼り付いて動く（縦横とも）
  stopFollowing(cam);
  panBy(cam, dx, dy);
  t.trail.push({ x: ev.clientX, y: ev.clientY, t: now });
  while (t.trail.length > 1 && now - t.trail[0].t > FLICK_WINDOW_MS) t.trail.shift();
});

canvas.addEventListener('pointerleave', () => {
  hover = null;
});

function endTouch(ev: PointerEvent, cancelled: boolean): void {
  const t = touches.get(ev.pointerId);
  touches.delete(ev.pointerId);
  if (touches.size < 2) pinchStart = null;
  if (!t || cancelled) return;

  // 動かした指は、離した時点の勢いで滑らせる（慣性は updateCamera 側）
  if (t.moved) {
    const now = performance.now();
    const from = t.trail[0];
    const elapsed = now - from.t;
    if (elapsed >= FLICK_MIN_MS && elapsed <= FLICK_WINDOW_MS * 3) {
      const sec = elapsed / 1000;
      flingBy(cam, (ev.clientX - from.x) / sec, (ev.clientY - from.y) / sec);
    } else {
      // 指を止めてから離した。そこで留める
      cam.vx = 0;
      cam.vy = 0;
    }
    return;
  }
  // 動かしていなければタップ
  cam.vx = 0;
  cam.vy = 0;
  const now = performance.now();
  // 編集中は連打で描くので、ダブルタップに倍率を取られては困る
  const isDouble = !editBrush && now - lastTapAt < DOUBLE_TAP_MS;
  lastTapAt = now;
  const w = screenToWorld(canvas, cam, ev.clientX, ev.clientY);
  if (isDouble) {
    // ダブルタップで拡大／全体を行き来する
    cycleZoom(cam);
    zoomBtn.textContent = zoomLabel(cam);
    return;
  }
  tap(w.x, w.y);
}

canvas.addEventListener('pointerup', (ev) => endTouch(ev, false));
canvas.addEventListener('pointercancel', (ev) => endTouch(ev, true));

function followCat(id: number): void {
  if (selectedCatId === id) {
    selectedCatId = null;
    stopFollowing(cam);
  } else {
    selectedCatId = id;
    cam.follow = id;
  }
}

function tap(wx: number, wy: number): void {
  const { c, r } = worldToCell(wx, wy);
  const grid = world.state.grid;

  // 編集中は、タップは全部「盤面を描く」になる（§6）
  if (editBrush) {
    if (!editRect) {
      paint(world, c, r, editBrush);
      return;
    }
    if (!rectFrom) {
      rectFrom = { c, r };
      return;
    }
    paintRect(world, rectFrom, { c, r }, editBrush);
    rectFrom = null;
    return;
  }

  // 落ちている資材を拾う（§10.8）。何より先に見る。
  // 猫は拾わないので、ここだけはプレイヤーが手を出す場所になっている
  const got = pickUpNear(world, c, r);
  if (got) {
    toast(`${describeBundle(got)} を拾った`);
    refreshHud();
    return;
  }

  // 見つかった思い出をタップ → その一行をもう一度読む（§1）
  const memory = world.state.memories.find((m) => m.found && m.c === c && m.r === r);
  if (memory) {
    toast(memory.line);
    return;
  }

  // 下見中の物は、同じマスをもう一度押したら建てる。
  // 猫を追う判定より先に見るのは、土台のマスに猫が乗っていても建てられるようにするため
  const confirming = heldItem !== null && ghost?.c === c && ghost?.r === r;

  // 猫が立っているマスをタップ → その猫を追いかける（カメラだけ。命令はしない。§2.3）
  // そうでなければ設置。こうしないと、猫の多い1階でタップが全部猫に吸われる
  const hit = world.state.cats.find(
    (k) => catHit(k, wx, wy) && Math.floor(k.x / TILE) === c && Math.floor((k.y - 1) / TILE) === r,
  );
  if (hit && !confirming) {
    followCat(hit.id);
    return;
  }

  // 置いてある物は、もう一度押すとどける（飾り → 家具 → 足場）
  if (!confirming && hasPlayerThing(c, r)) {
    const res = remove(world, c, r);
    if (res.ok) refreshHud();
    ghost = null;
    return;
  }

  if (heldItem === null) return;
  const def = itemDef(heldItem);

  if (!confirming && def.kind !== 'decor' && isSolidItem(heldItem) && tileAt(grid, c, r) !== EMPTY) {
    return;
  }

  // 複数マスの物は下見してから建てる
  if (!confirming && def.shape) {
    ghost = { c, r };
    const look = canPlaceItem(world, c, r, heldItem);
    if (!look.ok) toast(look.reason);
    return;
  }
  ghost = null;

  const before = new Set(reachableFor(world, view.species).keys());
  const res = place(world, c, r, heldItem);
  if (!res.ok) {
    toast(res.reason);
    return;
  }
  refreshHud();
  for (const k of reachableFor(world, view.species).keys()) {
    if (before.has(k)) continue;
    flashes.push({ c: k % grid.cols, r: Math.floor(k / grid.cols), t: 0 });
  }
}

/** そのマスにプレイヤーが置いた物があるか（どけられるか） */
function hasPlayerThing(c: number, r: number): boolean {
  const grid = world.state.grid;
  return (
    grid.decor[r * grid.cols + c] > 0 ||
    furnitureAt(grid, c, r) !== NO_FURNITURE ||
    isPlayerPlaced(grid, c, r)
  );
}

function selectItem(id: ItemId | null): void {
  heldItem = id;
  ghost = null;
  flipBtn.hidden = id === null || !itemHasFacing(id);
  refreshInventory();
}

flipBtn.addEventListener('click', () => {
  world.placeFlip = !world.placeFlip;
  ghost = null;
  flipBtn.textContent = world.placeFlip ? 'むき ◀' : 'むき ▶';
});

// ---- つくる（クラフト）--------------------------------------------------
//
// 作るのと置くのを分けてある。資材があれば作れて、置き場所は後から決める。

function openCraft(open: boolean): void {
  craftSheet.hidden = !open;
  if (open) {
    foundSheet.hidden = true;
    refreshRecipes();
  }
}
craftOpenBtn.addEventListener('click', () => openCraft(craftSheet.hidden));
craftCloseBtn.addEventListener('click', () => openCraft(false));

// ---- みつけた物（§10.18）------------------------------------------------
//
// 「あと何個」も「???」も出さない。まだ出ていない物の枠を見せた瞬間、
// 猫の世話が物語のための作業になる。ここに並ぶのは、もう出た物だけ。

const FORM_LABEL: Record<string, string> = {
  sign: 'しるし',
  readable: '読み物',
  keepsake: 'かたみ',
};

function openFound(open: boolean): void {
  foundSheet.hidden = !open;
  if (open) {
    openCraft(false);
    refreshFound();
  }
}
foundOpenBtn.addEventListener('click', () => openFound(foundSheet.hidden));
foundCloseBtn.addEventListener('click', () => openFound(false));

function floorNameAt(row: number): string {
  let best: { name: string; d: number } | null = null;
  for (const floor of BUILDING.floors) {
    const d = floor.standRow - row;
    if (d < 0 || d > 5) continue;
    if (!best || d < best.d) best = { name: floor.name, d };
  }
  return best?.name ?? '';
}

function refreshFound(): void {
  foundListEl.replaceChildren();
  const found = world.state.memories.filter((m) => m.found);
  if (!found.length) {
    const empty = document.createElement('div');
    empty.className = 'empty';
    empty.textContent =
      'まだ何も出ていない。猫が長く居ついた場所や、猫が壊した物の中から出てくる。';
    foundListEl.appendChild(empty);
    return;
  }

  // 下の階から並べる。この建物の話は下から上へ進む
  for (const floor of BUILDING.floors) {
    const here = found.filter((m) => floorNameAt(m.r) === floor.name);
    if (!here.length) continue;
    const group = document.createElement('div');
    group.className = 'floor-group';
    const name = document.createElement('div');
    name.className = 'floor-name';
    name.textContent = floor.name;
    group.appendChild(name);

    for (const m of here) {
      const entry = document.createElement('div');
      entry.className = 'entry';
      const form = document.createElement('div');
      form.className = `form ${m.form}`;
      form.textContent = FORM_LABEL[m.form] ?? '';
      const body = document.createElement('div');
      body.className = 'body';
      if (m.title) {
        const title = document.createElement('div');
        title.className = 'title';
        title.textContent = m.title;
        body.appendChild(title);
      }
      const line = document.createElement('div');
      line.className = 'line';
      line.textContent = m.line;
      body.appendChild(line);
      entry.append(form, body);
      group.appendChild(entry);
    }
    foundListEl.appendChild(group);
  }
}

function refreshRecipes(): void {
  recipesEl.replaceChildren();
  for (const def of ITEMS) {
    // まだ手に入れていない物はレシピごと出さない（§3.7 開通の報酬）
    if (!world.state.unlockedItems.includes(def.id)) continue;
    const btn = document.createElement('button');
    btn.className = 'recipe';
    const held = inventoryCount(world.state, def.id);
    btn.innerHTML =
      `<span class="name">${def.name}</span>` +
      `<span class="cost">${describeBundle(itemCost(def.id))}</span>` +
      `<span class="hint">${def.hint}</span>` +
      `<span class="have">${held > 0 ? `棚に${held}` : ''}</span>`;
    btn.disabled = !canCraftNow(def.id);
    btn.addEventListener('click', () => {
      const res = craft(world, def.id);
      if (!res.ok) {
        toast(res.reason);
        return;
      }
      toast(`${def.name} をつくった`);
      if (heldItem === null) selectItem(def.id);
      refreshHud();
      refreshRecipes();
    });
    recipesEl.appendChild(btn);
  }
}

function canCraftNow(id: ItemId): boolean {
  const cost = itemCost(id);
  return MATERIAL_IDS.every((m) => world.state.materials[m] >= (cost[m] ?? 0));
}

// ---- 棚（インベントリ）--------------------------------------------------

function refreshInventory(): void {
  inventoryEl.replaceChildren();
  const owned = ITEMS.filter((d) => inventoryCount(world.state, d.id) > 0);
  if (!owned.length) {
    const empty = document.createElement('div');
    empty.className = 'empty';
    empty.textContent = '棚は空。資材を拾って「つくる」から作る';
    inventoryEl.appendChild(empty);
    if (heldItem !== null) selectItem(null);
    return;
  }
  if (heldItem !== null && inventoryCount(world.state, heldItem) <= 0) {
    heldItem = null;
    ghost = null;
    flipBtn.hidden = true;
  }
  for (const def of owned) {
    const btn = document.createElement('button');
    btn.className = 'slot' + (heldItem === def.id ? ' on' : '');
    btn.innerHTML = `${def.name}<span class="n">${inventoryCount(world.state, def.id)}</span>`;
    btn.addEventListener('click', () => selectItem(heldItem === def.id ? null : def.id));
    inventoryEl.appendChild(btn);
  }
}

// ---- 資材の表示 ---------------------------------------------------------

function refreshMaterials(): void {
  materialsEl.replaceChildren();
  for (const kind of MATERIALS) {
    const n = world.state.materials[kind.id];
    const cell = document.createElement('div');
    cell.className = 'cell mat' + (n === 0 ? ' zero' : '');
    cell.innerHTML = `<div class="k">${kind.name}</div><div class="v">${n}</div>`;
    materialsEl.appendChild(cell);
  }
}

/** 資材・棚・レシピをまとめて描き直す。状態を変えた後に呼ぶ */
function refreshHud(): void {
  refreshMaterials();
  refreshInventory();
  if (!craftSheet.hidden) refreshRecipes();
  if (!foundSheet.hidden) refreshFound();
}

undoBtn.addEventListener('click', () => {
  if (!undoWorld(world)) toast('もどせる操作がない');
});
zoomBtn.addEventListener('click', () => {
  cycleZoom(cam);
  zoomBtn.textContent = zoomLabel(cam);
});

// ---- セーブ（§4.1）----------------------------------------------------
let saveTimer: number | undefined;
function scheduleSave(): void {
  window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    void saveSlot(AUTO_SLOT, world.state);
    world.dirty = false;
  }, 800);
}
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') void saveSlot(AUTO_SLOT, world.state);
});

/**
 * 開いた時にどこを見せるか。
 * 既定が全体像だと、猫の顔も落とし物も読めないまま「ただの図面」になる（§2.1）。
 * 寄った状態で、猫が居るあたりを映す。
 */
function focusOnCats(): void {
  const cats = world.state.cats;
  const grid = world.state.grid;
  const x = cats.length
    ? cats.reduce((sum, c) => sum + c.x, 0) / cats.length
    : (grid.cols * TILE) / 2;
  const y = cats.length
    ? cats.reduce((sum, c) => sum + c.y, 0) / cats.length
    : BUILDING.floors[0].standRow * TILE;
  // 床が画面のやや下に来るように、少し上を向く
  pendingCenter = { x, y: y - TILE * 2 };
}

function adoptState(state: GameState): void {
  world = createWorld(state);
  staticLayer = bakeStatic(world.state, staticLayer);
  flashes = [];
  selectedCatId = null;
  heldItem = null;
  ghost = null;
  refreshHud();
  resize();
  focusOnCats();
}

// ---- ループ（§4.5）----------------------------------------------------
let last = performance.now();
let stepRequest = 0;
/** 次のフレームで中心に持ってくる論理座標。倍率が確定してから当てる */
let pendingCenter: { x: number; y: number } | null = null;

function frame(now: number): void {
  const dt = Math.min(0.05, (now - last) / 1000);
  last = now;

  const before = world.state.cats.length;
  const ticks = speed > 0 ? speed : stepRequest;
  stepRequest = 0;
  for (let i = 0; i < ticks; i++) tick(world);
  if (world.state.cats.length > before) {
    for (const cat of world.state.cats.slice(before)) historyFade.set(cat.id, 5);
  }

  // 思い出が見つかったら、その一行がそっと出る。通知欄もログも作らない（§1）
  for (const found of world.justFound) {
    toast(found.title ? `${found.title} — ${found.line}` : found.line);
    flashes.push({ c: found.c, r: found.r, t: 0 });
  }
  if (world.justFound.length) {
    world.justFound.length = 0;
    if (!foundSheet.hidden) refreshFound();
  }

  // 何かが壊れたら、その場所を光らせる（§2.1 通知ではなく絵で）
  for (const broke of world.justBroke) {
    flashes.push({
      c: broke.key % world.state.grid.cols,
      r: Math.floor(broke.key / world.state.grid.cols),
      t: 0,
    });
  }
  world.justBroke.length = 0;
  world.justOpened.length = 0;

  if (world.dirty) scheduleSave();
  render(dt);
  requestAnimationFrame(frame);
}

function render(dt: number): void {
  const grid = world.state.grid;
  const followed = cam.follow !== null ? world.state.cats.find((c) => c.id === cam.follow) : null;
  if (cam.follow !== null && !followed) stopFollowing(cam);
  updateCamera(
    cam,
    { cssW, cssH, dpr },
    grid.cols,
    grid.rows,
    dt,
    followed ? { x: followed.x, y: followed.y - TILE * 0.4 } : null,
    touches.size > 0,
  );

  // 起動直後・ロード直後だけ、猫の居るあたりへ寄せる（倍率が決まってから）
  if (pendingCenter) {
    centerOn(cam, { cssW, cssH, dpr }, pendingCenter.x, pendingCenter.y);
    pendingCenter = null;
    updateCamera(cam, { cssW, cssH, dpr }, grid.cols, grid.rows, 0, null, false);
  }

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  const s = cam.scale * dpr;
  ctx.setTransform(s, 0, 0, s, -cam.x * s, -cam.y * s);

  staticLayer = bakeStatic(world.state, staticLayer);
  // 空は時間帯で変わるので焼かずに毎フレーム。建物は焼いたものを貼る（§5.2）
  drawSky(ctx, world.state, grid.cols * TILE);
  ctx.drawImage(staticLayer.canvas, 0, 0);
  drawSun(ctx, world.state);
  drawEntrance(ctx, world.state);
  drawTraces(ctx, world.state);
  // 壁の飾りは奥にあるので、置き物より先に描く
  drawDecor(ctx, grid);
  drawFixtures(ctx, world.state);
  drawSalvage(ctx, world.state);
  drawPlaced(ctx, grid);
  drawBuilds(ctx, world.state);
  drawMemories(ctx, world.state);
  drawBeds(ctx, grid);
  // 落ちている資材は家具より手前。拾える物が家具に隠れると気づけない
  drawDrops(ctx, world.state, world.state.time);
  drawBowl(ctx, world.state);
  drawWater(ctx, world.state);

  if (DEBUG_ENABLED && view.showTraffic) drawTraffic(ctx, world.state, world.traffic);

  if (DEBUG_ENABLED && (view.showEdges || view.showReach || view.showNodes)) {
    drawGraph(ctx, world.state, graphFor(world, view.species), reachableFor(world, view.species), view);
  }

  flashes = drawFlashes(ctx, flashes, dt);

  // 窓の外で待っている次の猫。入って来られるようにするのがプレイヤーの仕事
  const waiting = world.species.get(world.state.nextArrival);
  if (waiting) {
    drawWaitingCat(
      ctx,
      waiting,
      (world.state.entrance.c + 1) * TILE + 12,
      (world.state.entrance.r + 1) * TILE,
      world.state.time,
    );
  }

  for (const cat of world.state.cats) {
    drawCat(ctx, cat, world.species.get(cat.species)!, cat.id === selectedCatId);
  }
  if (followed) drawFollowMark(ctx, followed);

  // 名前と気持ち。寄っている時だけ出す（全体表示で全部出すと字だらけになる）
  const showNames = cam.scale >= 0.85;
  for (const cat of world.state.cats) {
    const picked = cat.id === selectedCatId;
    if (showNames || picked) drawName(ctx, cat, picked);
    if (showNames || picked) drawMood(ctx, cat, moodOf(cat));
  }

  // 来た直後の猫にだけ、来歴が一行ぶら下がる（§1）
  for (const cat of world.state.cats) {
    if (cat.arriving > 0) drawHistoryLine(ctx, cat, 1);
    else if (cat.arriving === 0 && historyFade.has(cat.id)) {
      const left = historyFade.get(cat.id)! - dt;
      if (left <= 0) historyFade.delete(cat.id);
      else {
        historyFade.set(cat.id, left);
        drawHistoryLine(ctx, cat, Math.min(1, left / 1.2));
      }
    }
  }

  const selected = world.state.cats.find((c) => c.id === selectedCatId);
  if (selected) {
    drawCatPath(ctx, world.state, selected);
    drawHistoryLine(ctx, selected, 1);
  }

  // 下見中の物（複数マスの家具）
  if (ghost && heldItem !== null) {
    const ok = canPlaceItem(world, ghost.c, ghost.r, heldItem).ok;
    for (const cell of itemCells(heldItem, ghost.c, ghost.r, world.placeFlip)) {
      drawCursor(ctx, cell.c, cell.r, ok);
    }
  }

  if (editBrush && rectFrom) drawCursor(ctx, rectFrom.c, rectFrom.r, true);
  if (editBrush?.kind === 'memory' && world.heldMemory) {
    const held = world.state.memories.find((m) => m.id === world.heldMemory);
    if (held) drawCursor(ctx, held.c, held.r, true);
  }
  if (hover && editBrush) {
    drawCursor(ctx, hover.c, hover.r, editBrush.kind !== 'erase');
  } else if (hover && heldItem !== null) {
    if (hasPlayerThing(hover.c, hover.r)) {
      drawCursor(ctx, hover.c, hover.r, true); // どけられる
    } else {
      const ok = canPlaceItem(world, hover.c, hover.r, heldItem).ok;
      for (const cell of itemCells(heldItem, hover.c, hover.r, world.placeFlip)) {
        drawCursor(ctx, cell.c, cell.r, ok);
      }
    }
  } else if (hover && hasPlayerThing(hover.c, hover.r)) {
    drawCursor(ctx, hover.c, hover.r, true);
  }

  drawFloorLabels(ctx, BUILDING.floors);

  catCountEl.textContent = String(world.state.cats.length);
  undoBtn.disabled = world.undoStack.length === 0;
}

// ---- デバッグパネルへ渡す口（§6）--------------------------------------
export interface AppApi {
  getWorld(): World;
  camera: Camera;
  place(c: number, r: number, item: ItemId): unknown;
  getSpeed(): number;
  setSpeed(n: number): void;
  stepOnce(): void;
  view: OverlayOptions & { species: string };
  getSelectedCatId(): number | null;
  selectCat(id: number | null): void;
  reset(): void;
  addCat(speciesId: string): void;
  removeSelectedCat(): void;
  setParam(speciesId: string, key: 'maxUp' | 'maxSide' | 'maxFall' | 'speed', value: number): void;
  /** ステージ編集（§6）。本番ビルドからは呼ばれない */
  editor: {
    getBrush(): Brush | null;
    setBrush(b: Brush | null): void;
    getRect(): boolean;
    setRect(on: boolean): void;
    validate(): Issue[];
    floorYields(): FloorYield[];
    checkClimbable(): ClimbCheck[];
    exportFloors(): string;
    exportMemories(): string;
    heldMemory(): string | null;
  };
  save(slot: string): Promise<void>;
  load(slot: string): Promise<boolean>;
  slots(): Promise<string[]>;
  fixedDt: number;
}

const api: AppApi = {
  getWorld: () => world,
  camera: cam,
  place: (c: number, r: number, item: ItemId) => place(world, c, r, item),
  getSpeed: () => speed,
  setSpeed: (n) => {
    speed = n;
  },
  stepOnce: () => {
    stepRequest = 1;
  },
  view,
  getSelectedCatId: () => selectedCatId,
  selectCat: (id) => {
    selectedCatId = id;
  },
  reset: () => adoptState(createInitialState()),
  addCat: (speciesId) => {
    const start = BUILDING.startCats[0];
    addCatToWorld(world, speciesId, start.c, start.r);
  },
  removeSelectedCat: () => {
    if (selectedCatId === null) return;
    world.state.cats = world.state.cats.filter((c) => c.id !== selectedCatId);
    world.reachCache.clear();
    selectedCatId = null;
  },
  setParam: (speciesId, key, value) => {
    setSpeciesParams(world, speciesId, { [key]: value });
  },
  editor: {
    getBrush: () => editBrush,
    setBrush: (b) => {
      editBrush = b;
      ghost = null;
      rectFrom = null;
      if (b) selectItem(null);
    },
    getRect: () => editRect,
    setRect: (on) => {
      editRect = on;
      rectFrom = null;
    },
    validate: () => validate(world),
    floorYields: () => floorYields(world),
    checkClimbable: () => checkClimbable(world),
    exportFloors: () => exportFloors(world),
    exportMemories: () => exportMemories(world),
    heldMemory: () => world.heldMemory,
  },
  save: (slot) => saveSlot(slot, world.state),
  load: async (slot) => {
    const s = await loadSlot(slot);
    if (!s) return false;
    adoptState(s);
    return true;
  },
  slots: listSlots,
  fixedDt: FIXED_DT,
};

// ---- 道具（デバッグパネル） -------------------------------------------
// 「道具」ボタンで開け閉めする。押されるまで React のチャンクは取りに行かないので、
// 遊ぶだけの人には降ってこない（§4.2）。
let toolsRoot: { unmount: () => void } | null = null;

async function openTools(withEditor: boolean): Promise<void> {
  if (toolsRoot && withEditor) {
    // #edit で来た時に、既に閉じた状態で載っていると開けないので載せ直す
    toolsRoot.unmount();
    toolsRoot = null;
  }
  if (!toolsRoot) {
    // コンソールから状態を覗けるようにしておく（§6 の道具のひとつ）
    (window as unknown as { catTower: AppApi }).catTower = api;
    const mod = await import('./debug/mount');
    toolsRoot = mod.mountDebug(api, debugRoot, withEditor);
  }
  debugRoot.hidden = false;
  DEBUG_ENABLED = true;
  view.showReach = true;
  toolsBtn.classList.add('on');
  toolsBtn.textContent = '道具をとじる';
  // パネルは Canvas と説明文の下にあるので、押したらそこまで送る。
  // #edit の時は、もっと下の「ステージ編集」までパネル自身が送る
  if (!withEditor) {
    window.setTimeout(() => debugRoot.scrollIntoView({ block: 'start' }), 0);
  }
}

function closeTools(): void {
  debugRoot.hidden = true;
  DEBUG_ENABLED = false;
  // 閉じたのに筆が残っていると、遊ぶつもりのタップで地形が変わる
  api.editor.setBrush(null);
  toolsBtn.classList.remove('on');
  toolsBtn.textContent = '道具';
  window.scrollTo({ top: 0 });
}

// ---- 起動 -------------------------------------------------------------
async function boot(): Promise<void> {
  const saved = await loadSlot(AUTO_SLOT);
  if (saved) adoptState(saved);
  refreshHud();
  resize();
  zoomBtn.textContent = zoomLabel(cam);
  focusOnCats();

  if (DEBUG_AT_BOOT) await openTools(EDIT_HASH);
  toolsBtn.addEventListener('click', () => {
    void (DEBUG_ENABLED ? closeTools() : openTools(false));
  });

  // アドレスバーで #edit を足しただけでは、同じ文書のままなので何も起きない
  window.addEventListener('hashchange', () => {
    if (location.hash === '#edit' || location.hash === '#debug') {
      void openTools(location.hash === '#edit');
    }
  });

  last = performance.now();
  requestAnimationFrame(frame);
}

void boot();
